import {QueryClient, QueryClientProvider} from '@tanstack/react-query'
import {ThemeProvider} from '@mui/material/styles'
import CssBaseline from '@mui/material/CssBaseline'
import {ReportModalProvider} from './contexts/ReportContext'
import {LayerPanelProvider} from './contexts/LayerPanelContext'
import ReportModal from './components/ReportModal'
import BackendBadge from './components/BackendBadge'
import {createTheme} from '@mui/material';

import {BrowserRouter, Navigate, Route, Routes, useParams} from 'react-router-dom'
import React, {lazy, Suspense} from 'react'

import {StrategyProvider} from './contexts/StrategyContext'
import {DateProvider} from './contexts/DateContext'
import {AuthProvider} from './contexts/AuthContext'
import {DrawingProvider} from './contexts/DrawingContext'
import {SelectionProvider} from './contexts/SelectionContext';
import {SimulationProvider} from './contexts/SimulationContext';

import ProtectedRoute from './components/ProtectedRoute'
import LoginPage from './pages/LoginPage'
import ProjectListingPage from './pages/ProjectListingPage'
import ProjectPage from './pages/ProjectPage'
import CreateProjectPage from './pages/CreateProjectPage'
import BulkCreateProjectPage from './pages/BulkCreateProjectPage'
import AdminPage from './pages/AdminPage'
import DetectPage from './pages/DetectPage'
import DataManagementPage from './pages/DataManagementPage'
import VegetationManagementPage from './pages/VegetationManagementPage'
import MyAccountPage from './pages/MyAccountPage'
import IncidentLogPage from './pages/IncidentLogPage'
import StoryMapsPage from './pages/StoryMapsPage'
import StoryMapBuilderPage from './pages/StoryMapBuilderPage'
import StoryViewerPage from './pages/StoryViewerPage'
// Lazy: this page pulls in jsPDF (plus canvg and core-js) — a substantial
// download and parse cost that every user of the app would otherwise pay on
// first load, for a page most of them never open.
const StoryPrintPage = lazy(() => import('./pages/StoryPrintPage'))
import {TimezoneProvider} from "./contexts/TimezoneContext.tsx";
import { ProjectConfigProvider } from './contexts/ProjectConfigContext';

const queryClient = new QueryClient()

// Opt-out of the small-screen UI scale (index.css --ui-zoom): the embed is
// an iframe sized by its host, and the print page is captured for the PDF.
const Unzoomed = ({children}: {children: React.ReactNode}) => (
    <div className="ui-unzoomed">{children}</div>
)

const LegacyUserRedirect = () => {
    const {userId} = useParams<{ userId: string }>()
    return <Navigate to={`/admin/people/${userId}`} replace/>
}

const theme = createTheme({
    palette: {
        primary: {
            main: '#212b37',
        },
        secondary: {
            main: '#00d4ad',
        },
    },
});

// Wrapper component that combines AuthProvider and BrowserRouter
const AppRoutes = () => {
    return (
        <BrowserRouter>
            <AuthProvider>
                <ProjectConfigProvider>
                    <ReportModalProvider>
                    <LayerPanelProvider>
                        <TimezoneProvider>
                            <DateProvider>
                                <Routes>
                                <Route path="/login" element={<LoginPage/>}/>
                                {/* Organisation-specific entry: /login/<slug> offers that org's single sign-on. */}
                                <Route path="/login/:slug" element={<LoginPage/>}/>
                                <Route
                                    path="/"
                                    element={
                                        <ProtectedRoute>
                                            <ProjectConfigProvider>
                                                <ProjectListingPage/>
                                            </ProjectConfigProvider>
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/projects"
                                    element={
                                        <ProtectedRoute>
                                            <SelectionProvider>
                                                <ProjectConfigProvider>
                                                    <ProjectListingPage/>
                                                </ProjectConfigProvider>
                                            </SelectionProvider>
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/projects/:projectId"
                                    element={
                                        <SimulationProvider>
                                            <ProtectedRoute>
                                                <SelectionProvider>
                                                    <StrategyProvider>
                                                        <DrawingProvider>
                                                            <ProjectPage/>
                                                        </DrawingProvider>
                                                    </StrategyProvider>
                                                </SelectionProvider>
                                            </ProtectedRoute>
                                        </SimulationProvider>
                                    }
                                />
                                {/* Vegetation editor: find things in imagery,
                                    paint and wand-edit the vegetation layer. */}
                                <Route
                                    path="/projects/:projectId/vegetation"
                                    element={
                                        <SimulationProvider>
                                            <ProtectedRoute>
                                                <SelectionProvider>
                                                    <StrategyProvider>
                                                        <DrawingProvider>
                                                            <ProjectPage/>
                                                        </DrawingProvider>
                                                    </StrategyProvider>
                                                </SelectionProvider>
                                            </ProtectedRoute>
                                        </SimulationProvider>
                                    }
                                />
                                {/* Incident deep link (notification emails, the
                                    Incidents tab): the project map with that
                                    incident selected and its detail modal open. */}
                                <Route
                                    path="/projects/:projectId/incidents/:incidentId"
                                    element={
                                        <SimulationProvider>
                                            <ProtectedRoute>
                                                <SelectionProvider>
                                                    <StrategyProvider>
                                                        <DrawingProvider>
                                                            <ProjectPage/>
                                                        </DrawingProvider>
                                                    </StrategyProvider>
                                                </SelectionProvider>
                                            </ProtectedRoute>
                                        </SimulationProvider>
                                    }
                                />
                                <Route
                                    path="/projects/:projectId/incidents/:incidentId/log"
                                    element={
                                        <ProtectedRoute>
                                            <IncidentLogPage/>
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/projects/:projectId/story-maps"
                                    element={
                                        <ProtectedRoute>
                                            <StoryMapsPage/>
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/projects/:projectId/story-maps/:storyMapId"
                                    element={
                                        <ProtectedRoute blockViewOnly>
                                            <StoryMapBuilderPage/>
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/projects/:projectId/story-maps/:storyMapId/preview"
                                    element={
                                        <ProtectedRoute>
                                            <StoryViewerPage/>
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/projects/:projectId/story-maps/:storyMapId/print"
                                    element={
                                        <ProtectedRoute>
                                            <Suspense fallback={<PrintPageFallback/>}>
                                                <Unzoomed><StoryPrintPage/></Unzoomed>
                                            </Suspense>
                                        </ProtectedRoute>
                                    }
                                />
                                {/* Story maps addressed by id alone: folder-level and
                                    organisation-wide stories have no project URL. The
                                    same pages serve both families (see storyPaths.ts). */}
                                <Route
                                    path="/story-maps/:storyMapId"
                                    element={
                                        <ProtectedRoute blockViewOnly>
                                            <StoryMapBuilderPage/>
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/story-maps/:storyMapId/preview"
                                    element={
                                        <ProtectedRoute>
                                            <StoryViewerPage/>
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/story-maps/:storyMapId/print"
                                    element={
                                        <ProtectedRoute>
                                            <Suspense fallback={<PrintPageFallback/>}>
                                                <Unzoomed><StoryPrintPage/></Unzoomed>
                                            </Suspense>
                                        </ProtectedRoute>
                                    }
                                />
                                {/* Public share link — no login, no Topbar */}
                                <Route path="/s/:slug" element={<StoryViewerPage/>}/>
                                {/* Embedded view: same reader, flat map imagery
                                    instead of a live Mapbox GL map per viewer.
                                    A distinct route rather than frame sniffing —
                                    see StoryViewerPage's isEmbed. */}
                                <Route path="/s/:slug/embed" element={<Unzoomed><StoryViewerPage/></Unzoomed>}/>
                                {/* Public print/PDF document (same /s/ auth-exempt prefix) */}
                                <Route
                                    path="/s/:slug/print"
                                    element={
                                        <Suspense fallback={<PrintPageFallback/>}>
                                            <Unzoomed><StoryPrintPage/></Unzoomed>
                                        </Suspense>
                                    }
                                />
                                                                <Route
                                    path="/create-project"
                                    element={
                                        <ProtectedRoute>
                                            <CreateProjectPage/>
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/create-projects-bulk"
                                    element={
                                        <ProtectedRoute>
                                            <BulkCreateProjectPage/>
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/detect"
                                    element={
                                        <ProtectedRoute>
                                            <DetectPage/>
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/data-management"
                                    element={
                                        <ProtectedRoute blockViewOnly>
                                            <DataManagementPage/>
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/vegetation-management"
                                    element={
                                        <ProtectedRoute blockViewOnly>
                                            <VegetationManagementPage/>
                                        </ProtectedRoute>
                                    }
                                />
                                {/* Admin area: Overview at /admin, one page per
                                    subject at /admin/<page>, and the person drawer
                                    at /admin/people/<id>. */}
                                <Route
                                    path="/admin"
                                    element={
                                        <ProtectedRoute>
                                            <AdminPage/>
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/admin/people/:userId"
                                    element={
                                        <ProtectedRoute>
                                            <AdminPage/>
                                        </ProtectedRoute>
                                    }
                                />
                                {/* The old per-user page lives on as a deep link. */}
                                <Route path="/admin/users/:userId" element={<LegacyUserRedirect/>}/>
                                <Route
                                    path="/admin/:page"
                                    element={
                                        <ProtectedRoute>
                                            <AdminPage/>
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/account"
                                    element={
                                        <ProtectedRoute>
                                            <MyAccountPage/>
                                        </ProtectedRoute>
                                    }
                                />
                            </Routes>
                            <ReportModal/>
                        </DateProvider>
                    </TimezoneProvider>
                    </LayerPanelProvider>
                </ReportModalProvider>
                </ProjectConfigProvider>
            </AuthProvider>
        </BrowserRouter>
    )
}

/** Shown while the lazily-loaded print bundle downloads. */
function PrintPageFallback() {
    return (
        <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: 'var(--vh100)',
            fontFamily: 'system-ui, sans-serif',
            color: '#5a6572',
        }}>
            Preparing document…
        </div>
    )
}

function App() {
    return (
        <QueryClientProvider client={queryClient}>
            <ThemeProvider theme={theme}>
                <CssBaseline/>
                <AppRoutes/>
                <BackendBadge/>
            </ThemeProvider>
        </QueryClientProvider>
    )
}

export default App
